import React, { useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import InputText from '@rsos/base-components/form/inputs/InputText';
import { InfoTip } from '@rsos/components/capstone/base/Tooltip/InfoTip/InfoTip';
import type { KeywordSet } from '@rsos/reduxDataStore';
import { KeywordChangeTracker } from '../../tracking/KeywordChangeTracker';
import { KeywordMultiInput } from '../KeywordMultiInput/KeywordMultiInput';
import { KeywordRecommendations } from '../KeywordRecommendations/KeywordRecommendations';
import {
  StyledButtonBox,
  StyledCheckbox,
  StyledFieldset,
  StyledFormButton,
  StyledFormContainer,
  StyledLabelText,
  StyledTierSelect,
} from './KeywordForm.styles';
import { keywordFormValidation } from './keywordFormValidation';

export type KeywordFormProps = {
  keywordSet: KeywordSet;
  save: (KeywordSet: KeywordSet) => void;
  cancel?: () => void;
  saveText?: string;
  className?: string;
};

const tiers = [
  { value: 'tier-1', label: 'Tier 1' },
  { value: 'tier-2', label: 'Tier 2' },
  { value: 'tier-3', label: 'Tier 3' },
];

export const KeywordForm = ({
  keywordSet,
  save,
  cancel,
  saveText = 'Save',
  className = '',
}: KeywordFormProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const tracker = useRef(new KeywordChangeTracker(keywordSet));
  const instrumentedSave = (newKeywordSet: KeywordSet) => {
    tracker.current.fireEvents(newKeywordSet);
    save(newKeywordSet);
  };
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <>
      <Formik<KeywordSet>
        initialValues={keywordSet}
        validationSchema={keywordFormValidation}
        onSubmit={instrumentedSave}
      >
        {({
          values,
          isValid,
          setFieldValue,
          errors,
          handleChange,
          touched,
          handleBlur,
        }) => {
          /**
           * The interface for the "speaker" value has an odd subtlety: you
           * must select at least one, but you can pick both. These methods
           * allow toggling checkboxes based on the string value of "speaker"
           * rather than boolean flags, while preventing the user from
           * unchecking all options.
           */
          const toggleCaller = () => {
            setFieldValue(
              'speaker',
              { '': 'operator', operator: '', caller: 'operator' }[
                values.speaker
              ],
            );
          };

          const toggleAgent = () => {
            setFieldValue(
              'speaker',
              { '': 'caller', operator: 'caller', caller: '' }[values.speaker],
            );
          };

          return (
            <Form>
              <StyledFormContainer className={className}>
                <label>
                  <StyledLabelText>
                    Keyword Topic{' '}
                    <InfoTip>
                      Set a topic for this grouping of keywords. Keyword Topic
                      will display when any matching keyword terms are detected
                      by the selected speaker(s).
                    </InfoTip>
                  </StyledLabelText>
                  <InputText
                    name="name"
                    inputRef={inputRef}
                    value={values.name}
                    error={touched.name ? errors.name : undefined}
                    onBlur={handleBlur}
                    shouldDisplayClearButton={false}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  <StyledLabelText>
                    Tier{' '}
                    <InfoTip>
                      Assign a tier to this keyword topic. Keyword tiers can be
                      used to organize keyword topics and also trigger
                      notifications.
                    </InfoTip>
                  </StyledLabelText>
                  <StyledTierSelect
                    options={tiers}
                    value={tiers.find(t => t.value === values.tier_id)}
                    isClearable={false}
                    onChange={(o: { value: KeywordSet['tier_id'] }) =>
                      setFieldValue('tier_id', o?.value)
                    }
                  />
                </label>

                <label>
                  <StyledLabelText>
                    Keyword Terms{' '}
                    <InfoTip>
                      Enter all terms or phrases that will result in a positive
                      keyword match for this topic. Keyword terms must be an
                      exact match to trigger the keyword topic.
                    </InfoTip>
                  </StyledLabelText>
                  <KeywordMultiInput
                    values={values.keywords}
                    onChange={vals => {
                      setFieldValue('keywords', Array.from(new Set(vals)));
                    }}
                    onBlur={handleBlur}
                    name="keywords"
                    errors={touched.keywords ? errors.keywords : undefined}
                    multiWordValues={true}
                  />
                  <KeywordRecommendations
                    onChange={(newSet, suggestion) => {
                      tracker.current.addSuggestion(
                        suggestion,
                        values.keywords.length,
                      );
                      setFieldValue('keywords', newSet);
                    }}
                    keywordSet={values}
                  />
                </label>
                <StyledFieldset>
                  <label>
                    <StyledLabelText>
                      Speaker{' '}
                      <InfoTip>
                        Select whether to detect keywords spoken by the caller,
                        the dispatcher, or both.
                      </InfoTip>
                    </StyledLabelText>
                  </label>
                  <StyledCheckbox
                    onChange={toggleCaller}
                    label="Caller"
                    isChecked={['caller', ''].includes(values.speaker)}
                  />{' '}
                  <StyledCheckbox
                    label="Dispatcher"
                    onChange={toggleAgent}
                    isChecked={['operator', ''].includes(values.speaker)}
                  />{' '}
                </StyledFieldset>

                <StyledButtonBox>
                  {cancel && (
                    <StyledFormButton
                      btnType="tertiary"
                      type="button"
                      onClick={cancel}
                    >
                      Cancel
                    </StyledFormButton>
                  )}
                  <StyledFormButton disabled={!isValid} type="submit">
                    {saveText}
                  </StyledFormButton>
                </StyledButtonBox>
              </StyledFormContainer>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
